import {
  FloatingArrow,
  FloatingPortal,
  useMergeRefs,
  useTransitionStyles,
} from '@floating-ui/react';
import { jsonAssignInlineVars } from '@vuddy/utils';
import clsx from 'clsx';
import { isNil } from 'lodash-es';
import { CSSProperties, HTMLProps, Ref } from 'react';
import { useZIndex } from '../hooks';
import { SurfaceColorToken, theme, TypoColorToken } from '../theme/theme.css';
import * as tooltipStyles from './tooltip.css';
import { useTooltipContext } from './useTooltipContext';

type TooltipContentProps = HTMLProps<HTMLDivElement> & {
  color?: TypoColorToken;
  bgColor?: SurfaceColorToken;
  arrow?: boolean;
  arrowStyle?: CSSProperties;
  fullWidth?: boolean;
  styles?: string;
  ref?: Ref<HTMLDivElement>;
};

export const TooltipContent = ({
  color = 'whitePrimary',
  bgColor = 'black',
  arrow = false,
  arrowStyle,
  fullWidth = false,
  children,
  ref: forwardedRef,
  ...props
}: TooltipContentProps) => {
  const { refs, strategy, x, y, context, getFloatingProps, open } =
    useTooltipContext();
  const { isMounted, styles: transitionStyles } = useTransitionStyles(context);
  const ref = useMergeRefs([refs.setFloating, forwardedRef]);
  const zIndex = useZIndex(isMounted && open);
  const { style: propsStyle, ...restProps } = props;
  return (
    <FloatingPortal>
      {isMounted && (
        <div
          ref={ref}
          className={clsx(
            tooltipStyles.contentWrapper({
              fullWidth,
            }),
            restProps.styles
          )}
          style={{
            ...jsonAssignInlineVars({
              [tooltipStyles.backgroundColorVar]: theme.surface[bgColor],
              [tooltipStyles.colorVar]: theme.text[color],
              [tooltipStyles.positonVar]: strategy,
              [tooltipStyles.visibilityVar]: isNil(x) ? 'hidden' : 'visible',
              [tooltipStyles.topVar]: (y ?? 0) + 'px',
              [tooltipStyles.leftVar]: (x ?? 0) + 'px',
              [tooltipStyles.zIndexVar]: zIndex,
              ...transitionStyles,
            }),
            ...propsStyle,
          }}
          {...getFloatingProps(restProps)}>
          {children}
          {arrow && (
            <div className={tooltipStyles.arrowWrapper} style={arrowStyle}>
              <FloatingArrow
                context={context}
                width={11}
                fill={bgColor}
                style={{
                  position: 'absolute',
                  width: '11px',
                  height: '11px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                }}
              />
            </div>
          )}
        </div>
      )}
    </FloatingPortal>
  );
};
